import type { UserRole } from '@onyx/types'
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiLogin, apiLogout, apiMe, apiChangePassword, type LoginType } from '@/api/backend'

export interface AuthUser {
  id: string
  username: string
  displayName: string
  email: string
  role: UserRole
  permissions?: string[]
  sales_rep_code?: string | null
  employee_id?: string | null
  must_change_password?: boolean
  is_active?: boolean
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null)
  const token = ref<string | null>(null)
  const loading = ref(false)

  const isAuthenticated = computed(() => !!token.value)
  const userRole = computed<UserRole>(() => (user.value?.role as UserRole) ?? 'viewer')
  const isSalesRep = computed(() => user.value?.role === 'sales_rep')
  const mustChangePassword = computed(() => !!user.value?.must_change_password)
  const displayName = computed(() => user.value?.displayName ?? user.value?.username ?? '')
  const initials = computed(() => {
    const name = displayName.value
    if (!name) return '?'
    const parts = name.split(' ')
    return parts.length >= 2
      ? (parts[0][0] + parts[1][0]).toUpperCase()
      : name.substring(0, 2).toUpperCase()
  })

  function hasPermission(_resource: string, action: 'read' | 'write' | 'delete' | 'export'): boolean {
    if (!user.value) return false
    if (user.value.role === 'admin') return true
    const roleMap: Record<UserRole, string[]> = {
      admin: ['*'],
      manager: ['read', 'write', 'export'],
      viewer: ['read'],
      sales_rep: ['read', 'export'],
      employee: ['read'],
    }
    return roleMap[user.value.role]?.includes(action) ?? false
  }

  function can(key: string): boolean {
    return !!user.value?.permissions?.includes(key)
  }

  async function login(username: string, password: string, loginType: LoginType = 'admin') {
    loading.value = true
    try {
      const resp = await apiLogin(username, password, loginType)
      token.value = resp.token
      user.value = resp.user as AuthUser
      localStorage.setItem('auth_token', resp.token)
      localStorage.setItem('auth_user', JSON.stringify(resp.user))
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    try {
      if (token.value) await apiLogout()
    } catch {
      // ignore
    } finally {
      user.value = null
      token.value = null
      localStorage.removeItem('auth_token')
      localStorage.removeItem('auth_user')
    }
  }

  async function init() {
    const savedToken = localStorage.getItem('auth_token')
    const savedUser = localStorage.getItem('auth_user')
    if (savedToken && savedUser) {
      token.value = savedToken
      user.value = JSON.parse(savedUser) as AuthUser
      try {
        const resp = await apiMe()
        user.value = resp.user as AuthUser
        localStorage.setItem('auth_user', JSON.stringify(resp.user))
      } catch {
        user.value = null
        token.value = null
        localStorage.removeItem('auth_token')
        localStorage.removeItem('auth_user')
      }
    }
  }

  async function changePassword(currentPassword: string, newPassword: string) {
    const resp = await apiChangePassword(currentPassword, newPassword)
    user.value = resp.user as AuthUser
    localStorage.setItem('auth_user', JSON.stringify(resp.user))
    return resp
  }

  return {
    user,
    token,
    loading,
    isAuthenticated,
    userRole,
    isSalesRep,
    mustChangePassword,
    displayName,
    initials,
    hasPermission,
    can,
    login,
    logout,
    init,
    changePassword,
  }
})
